import { useState, useEffect, useCallback } from "react";
import {
  Shield, Play, Check, X, Clock, AlertTriangle, RefreshCw,
  ChevronDown, ChevronUp, Terminal, Zap, Eye, Ban
} from "lucide-react";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

const RISK_STYLES = {
  READ_ONLY: { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30", label: "Lecture seule" },
  SAFE_WRITE: { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30", label: "Ecriture safe" },
  DANGEROUS: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30", label: "Dangereux" },
};

const STATUS_STYLES = {
  PROPOSED: { bg: "bg-blue-500/20", text: "text-blue-400", icon: Clock },
  APPROVED: { bg: "bg-amber-500/20", text: "text-amber-400", icon: Check },
  EXECUTED: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: Play },
  FAILED: { bg: "bg-red-500/20", text: "text-red-400", icon: AlertTriangle },
  CANCELLED: { bg: "bg-slate-500/20", text: "text-slate-400", icon: Ban },
  EXPIRED: { bg: "bg-slate-500/20", text: "text-slate-500", icon: Clock },
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.PROPOSED;
  const Icon = style.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${style.bg} ${style.text}`}>
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
};

const RiskBadge = ({ level }) => {
  const style = RISK_STYLES[level] || RISK_STYLES.READ_ONLY;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
};

// ── Action Card in Pending List ──
const PendingCard = ({ proposal, onApprove, onExecute, onCancel }) => {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState("");
  const risk = RISK_STYLES[proposal.risk_level] || RISK_STYLES.SAFE_WRITE;

  return (
    <div data-testid={`pending-card-${proposal.proposal_id}`} className={`rounded-xl border ${risk.border} bg-slate-800/40 overflow-hidden`}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800/60 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${risk.bg}`}>
            <Zap className={`w-4 h-4 ${risk.text}`} />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{proposal.action_label}</div>
            <div className="text-xs text-slate-500 truncate">
              {proposal.proposal_id} &middot; {proposal.source} &middot; {proposal.age_minutes ? `${Math.round(proposal.age_minutes)}min` : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge status={proposal.status} />
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-700/50 space-y-3">
          {proposal.reason && (
            <div className="text-xs text-slate-400">
              <span className="text-slate-500">Raison :</span> {proposal.reason}
            </div>
          )}
          {Object.keys(proposal.params || {}).length > 0 && (
            <div className="bg-slate-900/50 rounded-lg p-2.5">
              <pre className="text-xs text-slate-400 overflow-auto">{JSON.stringify(proposal.params, null, 2)}</pre>
            </div>
          )}

          {proposal.status === "PROPOSED" && !proposal.expired && (
            <div className="space-y-2">
              {proposal.risk_level === "DANGEROUS" && (
                <input
                  type="text"
                  placeholder="Motif obligatoire pour action DANGEROUS..."
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder:text-slate-500"
                  data-testid="approve-reason-input"
                />
              )}
              <div className="flex gap-2">
                <button
                  data-testid={`approve-btn-${proposal.proposal_id}`}
                  onClick={() => onApprove(proposal.proposal_id, reason)}
                  disabled={proposal.risk_level === "DANGEROUS" && !reason}
                  className="flex-1 py-2 px-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" /> Approuver
                </button>
                <button
                  data-testid={`cancel-btn-${proposal.proposal_id}`}
                  onClick={() => onCancel(proposal.proposal_id)}
                  className="py-2 px-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center justify-center gap-1.5 transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> Annuler
                </button>
              </div>
            </div>
          )}

          {proposal.status === "APPROVED" && !proposal.expired && (
            <button
              data-testid={`execute-btn-${proposal.proposal_id}`}
              onClick={() => onExecute(proposal.proposal_id)}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Play className="w-4 h-4" /> Executer
            </button>
          )}

          {proposal.expired && (
            <div className="text-xs text-red-400 bg-red-500/10 rounded-lg p-2 text-center">
              Proposition expiree ({Math.round(proposal.age_minutes)} min)
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Log Entry ──
const LogEntry = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      data-testid={`log-entry-${entry.proposal_id || entry.action_id}`}
      className="flex items-center justify-between p-3 hover:bg-slate-800/40 rounded-lg cursor-pointer transition-colors"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <StatusBadge status={entry.status} />
        <div className="min-w-0">
          <span className="text-sm font-medium truncate block">{entry.action_label || entry.action_id}</span>
          <span className="text-xs text-slate-500 truncate block">
            {entry.proposed_by?.email || "?"} &middot; {entry.created_at ? new Date(entry.created_at).toLocaleString("fr-FR") : ""}
            {entry.duration_ms ? ` &middot; ${entry.duration_ms}ms` : ""}
          </span>
        </div>
      </div>
      {expanded && entry.result && (
        <div className="mt-2 bg-slate-900/50 rounded-lg p-2.5 w-full" onClick={e => e.stopPropagation()}>
          <pre className="text-xs text-slate-400 overflow-auto max-h-48">{JSON.stringify(entry.result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

// ── Main Console ──
export default function YaelConsole() {
  const [tab, setTab] = useState("pending");
  const [registry, setRegistry] = useState([]);
  const [pending, setPending] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [regResp, pendResp, logResp] = await Promise.all([
        authFetch(`${API}/admin/actions/registry`),
        authFetch(`${API}/admin/actions/pending`),
        authFetch(`${API}/admin/actions/log?limit=30`),
      ]);
      if (regResp.ok) setRegistry((await regResp.json()).actions || []);
      if (pendResp.ok) setPending((await pendResp.json()).proposals || []);
      if (logResp.ok) setLogs((await logResp.json()).logs || []);
    } catch (e) {
      console.error("YaelConsole refresh error:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const flash = (msg, type = "success") => {
    setActionMsg({ msg, type });
    setTimeout(() => setActionMsg(null), 4000);
  };

  const handleApprove = async (proposalId, reason) => {
    try {
      const resp = await authFetch(`${API}/admin/actions/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ proposal_id: proposalId, reason }),
      });
      if (resp.ok) {
        flash(`Proposition ${proposalId} approuvee`);
        refresh();
      } else {
        const err = await resp.json();
        flash(err.detail || "Erreur", "error");
      }
    } catch (e) { flash(e.message, "error"); }
  };

  const handleExecute = async (proposalId) => {
    try {
      const resp = await authFetch(`${API}/admin/actions/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ proposal_id: proposalId }),
      });
      if (resp.ok) {
        const data = await resp.json();
        flash(`Action executee (${data.duration_ms}ms) — statut: ${data.status}`);
        refresh();
      } else {
        const err = await resp.json();
        flash(err.detail || "Erreur execution", "error");
      }
    } catch (e) { flash(e.message, "error"); }
  };

  const handleCancel = async (proposalId) => {
    try {
      const resp = await authFetch(`${API}/admin/actions/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ proposal_id: proposalId }),
      });
      if (resp.ok) {
        flash(`Proposition ${proposalId} annulee`);
        refresh();
      } else {
        const err = await resp.json();
        flash(err.detail || "Erreur", "error");
      }
    } catch (e) { flash(e.message, "error"); }
  };

  const handlePropose = async (actionId) => {
    try {
      const resp = await authFetch(`${API}/admin/actions/propose`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action_id: actionId, source: "yael_console" }),
      });
      if (resp.ok) {
        const data = await resp.json();
        flash(`Proposition creee: ${data.proposal_id}`);
        setTab("pending");
        refresh();
      } else {
        const err = await resp.json();
        flash(err.detail || "Erreur", "error");
      }
    } catch (e) { flash(e.message, "error"); }
  };

  const pendingCount = pending.filter(p => !p.expired).length;
  const writeActions = registry.filter(a => a.risk_level !== "READ_ONLY");

  return (
    <div data-testid="yael-console" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border border-violet-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold" data-testid="yael-console-title">Console Yael</h2>
              <p className="text-sm text-slate-400">Orchestrateur d'actions admin — propose &rarr; approuve &rarr; execute</p>
            </div>
          </div>
          <button
            data-testid="yael-refresh-btn"
            onClick={refresh}
            disabled={loading}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Flash message */}
      {actionMsg && (
        <div className={`rounded-lg p-3 text-sm font-medium ${
          actionMsg.type === "error"
            ? "bg-red-500/20 text-red-300 border border-red-500/30"
            : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
        }`}>
          {actionMsg.msg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/50 rounded-xl p-1" data-testid="yael-tabs">
        {[
          { id: "pending", label: "En attente", badge: pendingCount },
          { id: "registry", label: "Actions disponibles", badge: writeActions.length },
          { id: "log", label: "Journal", badge: logs.length },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`yael-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              tab === t.id
                ? "bg-violet-600 text-white"
                : "text-slate-400 hover:text-white hover:bg-slate-700/50"
            }`}
          >
            {t.label}
            {t.badge > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                tab === t.id ? "bg-white/20" : "bg-slate-700"
              }`}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "pending" && (
        <div data-testid="yael-pending-list" className="space-y-3">
          {pending.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucune proposition en attente</p>
            </div>
          ) : (
            pending.map(p => (
              <PendingCard
                key={p.proposal_id}
                proposal={p}
                onApprove={handleApprove}
                onExecute={handleExecute}
                onCancel={handleCancel}
              />
            ))
          )}
        </div>
      )}

      {tab === "registry" && (
        <div data-testid="yael-registry-list" className="space-y-3">
          {registry.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Terminal className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucune action dans le registre</p>
            </div>
          ) : (
            <>
              {/* Group by category */}
              {["diagnostic", "correction", "maintenance"].map(cat => {
                const items = registry.filter(a => a.category === cat);
                if (items.length === 0) return null;
                return (
                  <div key={cat} className="space-y-2">
                    <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold px-1">
                      {cat === "diagnostic" ? "Diagnostic" : cat === "correction" ? "Corrections" : "Maintenance"}
                    </h3>
                    {items.map(action => (
                      <div
                        key={action.id}
                        data-testid={`registry-action-${action.id}`}
                        className="flex items-center justify-between p-3 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium">{action.label}</span>
                            <RiskBadge level={action.risk_level} />
                            {!action.enabled && (
                              <span className="px-1.5 py-0.5 rounded text-xs bg-slate-700 text-slate-500">desactive</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{action.description}</p>
                        </div>
                        {action.risk_level !== "READ_ONLY" && action.enabled && (
                          <button
                            data-testid={`propose-btn-${action.id}`}
                            onClick={() => handlePropose(action.id)}
                            className="ml-3 flex-shrink-0 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                          >
                            <Zap className="w-3 h-3" /> Proposer
                          </button>
                        )}
                        {action.risk_level === "READ_ONLY" && (
                          <button
                            data-testid={`run-btn-${action.id}`}
                            onClick={async () => {
                              try {
                                const endpoint = action.endpoint || `/api/admin/actions/execute`;
                                const resp = await authFetch(`${getApiUrl()}${endpoint}`);
                                if (resp.ok) {
                                  const data = await resp.json();
                                  flash(`${action.label} : OK`);
                                } else {
                                  flash(`Erreur ${resp.status}`, "error");
                                }
                              } catch (e) { flash(e.message, "error"); }
                            }}
                            className="ml-3 flex-shrink-0 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                          >
                            <Eye className="w-3 h-3" /> Lancer
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {tab === "log" && (
        <div data-testid="yael-log-list" className="bg-slate-800/30 rounded-xl border border-slate-700/50 divide-y divide-slate-700/30">
          {logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucune action dans le journal</p>
            </div>
          ) : (
            logs.map((entry, i) => <LogEntry key={entry.proposal_id || i} entry={entry} />)
          )}
        </div>
      )}
    </div>
  );
}
